import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import SelectDropdown from '../components/SelectDropdown';
import {
  WellnessSessionDraft,
  WellnessStep,
  createEmptyDraft,
  addStep,
  removeStep,
  updateStep,
  saveDraftToStorage,
  validateDraft,
  computeCompletionPercent
} from '../logic/wellnessCreation';
import { toast } from 'react-toastify';

const ConsultantWellnessCreationPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [draft, setDraft] = useState<WellnessSessionDraft>(createEmptyDraft());
  const [showPreview, setShowPreview] = useState(false);
  const [completionPercent, setCompletionPercent] = useState(0);
  const [isPublishing, setIsPublishing] = useState(false);

  // Update completion percentage when draft changes
  useEffect(() => {
    setCompletionPercent(computeCompletionPercent(draft));
  }, [draft]);

  // Handle basic session info changes
  const handleSessionChange = (field: keyof WellnessSessionDraft, value: any) => {
    setDraft(prev => ({
      ...prev,
      [field]: value,
      updatedAt: new Date().toISOString()
    }));
  };

  // Handle step changes
  const handleStepChange = (stepId: string, field: keyof WellnessStep, value: any) => {
    setDraft(prev => ({
      ...prev,
      steps: updateStep(prev.steps, stepId, { [field]: value }),
      updatedAt: new Date().toISOString()
    }));
  };



  // Handle theme color selection
  const handleThemeColorChange = (color: string) => {
    handleSessionChange('themeColor', color);
  };

  // Add a new step
  const handleAddStep = () => {
    setDraft(prev => ({
      ...prev,
      steps: addStep(prev.steps),
      updatedAt: new Date().toISOString()
    }));
  };

  // Remove a step
  const handleRemoveStep = (stepId: string) => {
    setDraft(prev => ({
      ...prev,
      steps: removeStep(prev.steps, stepId),
      updatedAt: new Date().toISOString()
    }));
  };

  // Save draft to localStorage
  const handleSaveDraft = () => {
    // Create a copy without file references for localStorage
    const draftForStorage = {
      ...draft,
      audioFile: undefined, // Don't persist files
      steps: draft.steps.map(step => ({
        ...step,
        imageUrl: undefined // Don't persist images
      }))
    };
    saveDraftToStorage(draftForStorage);
    toast.success('Draft saved successfully!');
  };

  // Preview session
  const handlePreview = () => {
    setShowPreview(true);
  };

  // Publish session to backend
  const handlePublish = async () => {
    const validation = validateDraft(draft);

    if (!validation.isValid) {
      toast.error(`Please fix the following errors: ${validation.errors.join(', ')}`);
      return;
    }

    if (isPublishing) return;
    setIsPublishing(true);

    try {
      // Get the auth token
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please log in again.');
        return;
      }

      // Prepare session data for API
      const sessionData = {
        title: draft.title,
        description: draft.description,
        category: draft.category,
        duration: draft.duration,
        steps: draft.steps,
        difficulty: draft.difficulty,
        videoUrl: draft.videoUrl,
        themeColor: draft.themeColor,
        breathingTimer: draft.breathingTimer,
        showProgress: draft.showProgress
      };

      const response = await fetch('/api/wellness-sessions/publish', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(sessionData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to publish session');
      }

      toast.success(`Session published successfully under "${draft.category}" category!`);
      
      // Reset the form after successful publication
      setDraft(createEmptyDraft());
      setShowPreview(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to publish session');
      console.error('Error publishing session:', error);
    } finally {
      setIsPublishing(false);
    }
  };

  // Reset form
  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset the form? All unsaved changes will be lost.')) {
      setDraft(createEmptyDraft());
      toast.info('Form reset');
    }
  };

  return (
    <div className={`consultant-wellness-creation ${isDarkMode ? 'dark' : ''}`}>
      <div className="consultant-wellness-creation__container">
        {/* Header */}
        <div className="consultant-wellness-creation__header">
          <h1 className="consultant-wellness-creation__title">Create Your Wellness Loop</h1>
          <p className="consultant-wellness-creation__subtitle">
            Structure your custom meditation, breathwork, or exercise session with precision.
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="consultant-wellness-creation__progress">
          <div className="consultant-wellness-creation__progress-header">
            <span className="consultant-wellness-creation__progress-label">Session Progress</span>
            <span className="consultant-wellness-creation__progress-percent">{completionPercent}% Complete</span>
          </div>
          <progress
            className="consultant-wellness-creation__progress-bar"
            value={completionPercent}
            max="100"
          />
        </div>

        {/* Basic Session Info Section */}
        <section className="consultant-wellness-creation__section">
          <div className="consultant-wellness-creation__section-header">
            <h2 className="consultant-wellness-creation__section-title">
              <span className="material-symbols-outlined consultant-wellness-creation__section-icon">info</span>
              Basic Session Info
            </h2>
          </div>
          <div className="consultant-wellness-creation__section-content">
            <div className="consultant-wellness-creation__form-grid">
              <div className="form-control">
                <label className="form-control__label" htmlFor="session-title">
                  Session Title <span className="consultant-wellness-creation__required">*</span>
                </label>
                <input
                  id="session-title"
                  type="text"
                  className="form-input"
                  placeholder="e.g. Morning Mindfulness Flow"
                  value={draft.title}
                  onChange={(e) => handleSessionChange('title', e.target.value)}
                  required
                />
              </div>

              <div className="form-control">
                <label className="form-control__label" htmlFor="session-duration">
                  Duration (minutes) <span className="consultant-wellness-creation__required">*</span>
                </label>
                <input
                  id="session-duration"
                  type="number"
                  className="form-input"
                  placeholder="15"
                  min="1"
                  value={draft.duration}
                  onChange={(e) => handleSessionChange('duration', parseInt(e.target.value) || 0)}
                  required
                />
              </div>

              <div className="form-control">
                <label className="form-control__label">Wellness Category</label>
                <SelectDropdown
                  value={draft.category}
                  onChange={(value) => handleSessionChange('category', value as WellnessSessionDraft['category'])}
                  options={[
                    { value: 'physical', label: 'Physical' },
                    { value: 'financial', label: 'Financial' },
                    { value: 'spiritual', label: 'Spiritual' },
                    { value: 'nutritional', label: 'Nutritional' },
                    { value: 'emotio-psycho-rational', label: 'Emotio-Psycho-Rational' },
                    { value: 'legendarial', label: 'Legendarial' },
                    { value: 'occupational', label: 'Occupational' },
                    { value: 'religio-traditio-cultural', label: 'Religio-Traditio-Cultural' },
                    { value: 'socio-relational', label: 'Socio-Relational' },
                    { value: 'environmental', label: 'Environmental' },
                  ]}
                  ariaLabel="Wellness category"
                />
              </div>

              <div className="form-control">
                <label className="form-control__label" htmlFor="session-video-link">Video Link (optional)</label>
                <input
                  id="session-video-link"
                  type="url"
                  className="form-input"
                  placeholder="https://youtube.com/..."
                  value={draft.videoUrl}
                  onChange={(e) => handleSessionChange('videoUrl', e.target.value)}
                />
              </div>

              <div className="consultant-wellness-creation__form-grid--single">
                <div className="form-control">
                  <label className="form-control__label" htmlFor="session-description">
                    Session Description/Objective <span className="consultant-wellness-creation__required">*</span>
                  </label>
                  <textarea
                    id="session-description"
                    className="form-textarea"
                    placeholder="Describe the goal of this session..."
                    rows={3}
                    value={draft.description}
                    onChange={(e) => handleSessionChange('description', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Step Builder Section */}
        <section className="consultant-wellness-creation__section">
          <div className="consultant-wellness-creation__section-header consultant-wellness-creation__step-builder">
            <h2 className="consultant-wellness-creation__section-title">
              <span className="material-symbols-outlined consultant-wellness-creation__section-icon">layers</span>
              Step Builder
            </h2>
            <div className="consultant-wellness-creation__step-count">
              <label className="consultant-wellness-creation__step-count-label">Number of Steps (1-10)</label>
              <input
                type="number"
                className="consultant-wellness-creation__step-count-input"
                min="1"
                max="10"
                value={draft.steps.length}
                onChange={(e) => {
                  const newCount = parseInt(e.target.value) || 1;
                  const clampedCount = Math.max(1, Math.min(10, newCount));

                  setDraft(prev => {
                    let newSteps = [...prev.steps];
                    while (newSteps.length < clampedCount) {
                      newSteps = addStep(newSteps);
                    }
                    while (newSteps.length > clampedCount) {
                      newSteps = newSteps.slice(0, clampedCount);
                    }
                    return {
                      ...prev,
                      steps: newSteps,
                      updatedAt: new Date().toISOString()
                    };
                  });
                }}
              />
            </div>
          </div>

          <div className="consultant-wellness-creation__section-content">
            {draft.steps.map((step, index) => (
              <div key={step.id} className="consultant-wellness-creation__step">
                <div className="consultant-wellness-creation__step-header">
                  <div className="consultant-wellness-creation__step-number">
                    <span className="consultant-wellness-creation__step-badge">{index + 1}</span>
                    <h3 className="consultant-wellness-creation__step-title">Step {index + 1}</h3>
                  </div>
                  <button
                    type="button"
                    className="consultant-wellness-creation__step-remove"
                    onClick={() => handleRemoveStep(step.id)}
                    aria-label={`Remove step ${index + 1}`}
                  >
                    <span className="material-symbols-outlined">delete</span>
                  </button>
                </div>

                <div className="consultant-wellness-creation__step-fields">
                  <div className="form-control">
                    <label className="form-control__label">Step Title</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Deep Breathing Initiation"
                      value={step.title}
                      onChange={(e) => handleStepChange(step.id, 'title', e.target.value)}
                    />
                  </div>

                  <div className="form-control">
                    <label className="form-control__label">Instructions</label>
                    <textarea
                      className="form-textarea"
                      placeholder="Sit comfortably and focus on the breath..."
                      rows={3}
                      value={step.instructions}
                      onChange={(e) => handleStepChange(step.id, 'instructions', e.target.value)}
                    />
                  </div>

                  <div className="consultant-wellness-creation__step-duration-row">
                    <div className="consultant-wellness-creation__step-duration-field form-control">
                      <label className="form-control__label">Duration (sec)</label>
                      <input
                        type="number"
                        className="form-input"
                        placeholder="60"
                        min="1"
                        value={step.duration}
                        onChange={(e) => handleStepChange(step.id, 'duration', parseInt(e.target.value) || 0)}
                      />
                    </div>

                    <label className="consultant-wellness-creation__step-timed-toggle">
                      <input
                        type="checkbox"
                        checked={step.isTimed}
                        onChange={(e) => handleStepChange(step.id, 'isTimed', e.target.checked)}
                      />
                      <span>Timed Step</span>
                    </label>
                  </div>

                </div>
              </div>
            ))}

            {draft.steps.length < 10 && (
              <button
                type="button"
                className="consultant-wellness-creation__add-step"
                onClick={handleAddStep}
              >
                <span className="material-symbols-outlined">add_circle</span>
                Add Another Step
              </button>
            )}
          </div>
        </section>

        {/* Media & Customization Section */}
        <section className="consultant-wellness-creation__section">
          <div className="consultant-wellness-creation__section-header">
            <h2 className="consultant-wellness-creation__section-title">
              <span className="material-symbols-outlined consultant-wellness-creation__section-icon">palette</span>
              Media &amp; Customization
            </h2>
          </div>
          <div className="consultant-wellness-creation__section-content">
            <div className="consultant-wellness-creation__form-grid">

              {/* Difficulty Level */}
              <div className="form-control">
                <label className="form-control__label">Difficulty Level</label>
                <div className="consultant-wellness-creation__difficulty-selector">
                  {(['Beginner', 'Intermediate', 'Advanced'] as const).map((level) => (
                    <button
                      key={level}
                      type="button"
                      className={`consultant-wellness-creation__difficulty-btn ${
                        draft.difficulty === level ? 'consultant-wellness-creation__difficulty-btn--active' : ''
                      }`}
                      onClick={() => handleSessionChange('difficulty', level)}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Features */}
              <div className="consultant-wellness-creation__form-grid--single">
                <div className="form-control">
                  <label className="form-control__label">Additional Features</label>
                  <div className="consultant-wellness-creation__feature-toggles">
                    <label className="consultant-wellness-creation__feature-toggle">
                      <input
                        type="checkbox"
                        checked={draft.breathingTimer}
                        onChange={(e) => handleSessionChange('breathingTimer', e.target.checked)}
                      />
                      <div className="consultant-wellness-creation__feature-content">
                        <span className="consultant-wellness-creation__feature-title">Enable Breathing Timer</span>
                        <span className="consultant-wellness-creation__feature-description">Adds a visual guide for inhale/exhale cycles</span>
                      </div>
                    </label>

                    <label className="consultant-wellness-creation__feature-toggle">
                      <input
                        type="checkbox"
                        checked={draft.showProgress}
                        onChange={(e) => handleSessionChange('showProgress', e.target.checked)}
                      />
                      <div className="consultant-wellness-creation__feature-content">
                        <span className="consultant-wellness-creation__feature-title">Show Progress Tracking</span>
                        <span className="consultant-wellness-creation__feature-description">Enables session logging for users</span>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Theme Color */}
              <div className="form-control">
                <label className="form-control__label">Theme Color</label>
                <div className="consultant-wellness-creation__theme-colors">
                  {[
                    '#2bd4bd', '#3b82f6', '#a855f7', '#f43f5e', '#f97316', '#10b981'
                  ].map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`consultant-wellness-creation__theme-color ${
                        draft.themeColor === color ? 'consultant-wellness-creation__theme-color--selected' : ''
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => handleThemeColorChange(color)}
                      aria-label={`Select theme color ${color}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Preview Modal */}
        {showPreview && (
          <div className="consultant-wellness-creation__preview-modal" onClick={() => setShowPreview(false)}>
            <div className="consultant-wellness-creation__preview-content" onClick={(e) => e.stopPropagation()}>
              <div className="consultant-wellness-creation__preview-header">
                <h2 className="consultant-wellness-creation__preview-title">Session Preview</h2>
                <button
                  className="consultant-wellness-creation__preview-close"
                  onClick={() => setShowPreview(false)}
                  aria-label="Close preview"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <div className="consultant-wellness-creation__preview-body">
                <div className="consultant-wellness-creation__preview-field">
                  <span className="consultant-wellness-creation__preview-label">Title</span>
                  <span className="consultant-wellness-creation__preview-value">{draft.title || 'Untitled'}</span>
                </div>

                <div className="consultant-wellness-creation__preview-field">
                  <span className="consultant-wellness-creation__preview-label">Duration</span>
                  <span className="consultant-wellness-creation__preview-value">{draft.duration} minutes</span>
                </div>

                <div className="consultant-wellness-creation__preview-field">
                  <span className="consultant-wellness-creation__preview-label">Category</span>
                  <span className="consultant-wellness-creation__preview-value">{draft.category}</span>
                </div>

                <div className="consultant-wellness-creation__preview-field">
                  <span className="consultant-wellness-creation__preview-label">Difficulty</span>
                  <span className="consultant-wellness-creation__preview-value">{draft.difficulty}</span>
                </div>

                <div className="consultant-wellness-creation__preview-field">
                  <span className="consultant-wellness-creation__preview-label">Description</span>
                  <span className="consultant-wellness-creation__preview-value">{draft.description || 'No description'}</span>
                </div>

                <div className="consultant-wellness-creation__preview-field">
                  <span className="consultant-wellness-creation__preview-label">Steps</span>
                  <span className="consultant-wellness-creation__preview-value">{draft.steps.length} steps</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sticky Footer */}
        <footer className="consultant-wellness-creation__footer">
          <div className="consultant-wellness-creation__footer-content">
            <div className="consultant-wellness-creation__footer-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handlePreview}
              >
                <span className="material-symbols-outlined consultant-wellness-creation__footer-icon">visibility</span>
                Preview Session
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleSaveDraft}
              >
                <span className="material-symbols-outlined consultant-wellness-creation__footer-icon">save</span>
                Save Draft
              </button>
            </div>

            <div className="consultant-wellness-creation__footer-main-actions">
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={handleReset}
              >
                <span className="material-symbols-outlined consultant-wellness-creation__footer-icon">restart_alt</span>
                Reset Form
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handlePublish}
                disabled={isPublishing}
              >
                <span className="material-symbols-outlined consultant-wellness-creation__footer-icon">
                  {isPublishing ? 'hourglass_empty' : 'rocket_launch'}
                </span>
                {isPublishing ? 'Publishing...' : 'Publish Session'}
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ConsultantWellnessCreationPage;
